/**
 * 용량 룰 검수 상세 페이지 (계획 C-9·C-10).
 *
 * 좌우 분할(react-resizable-panels):
 *  - 왼쪽: 룰 값 패널(DoseRuleSegmentPanel) + 메타 + 판정 바(승인/반려/재추출, 이전/다음)
 *  - 오른쪽: 허가사항 원문(DrugLabelInfo, 라벨 D1) + 사용자 검색(ContentSearchBar)
 * 인용문 클릭 → 오른쪽 원문에서 해당 문장 하이라이트(계획 D3, domHighlight 유틸).
 *
 * 라벨 데이터는 계약 5종 밖의 기존 API(useDrugProductLabel)로 로드하며, 실패해도
 * 왼쪽 판정은 계속 동작한다(계획 D12). itemCode는 목록 state 우선, 없으면 상세 응답 폴백(D2).
 */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { PanelGroup, Panel, PanelResizeHandle } from "react-resizable-panels";
import { COLOR_SYSTEM } from "components/design/design-system";
import Button from "components/atoms/Button";
import Spinner from "components/atoms/Spinner";
import DrugLabelInfo from "components/molecules/DrugLabelInfo";
import ContentSearchBar from "components/molecules/ContentSearchBar";
import { ConfirmManager } from "components/organisms/CommonUI";
import DoseRuleSegmentPanel from "components/organisms/DoseRuleSegmentPanel";
import DoseRuleVersionList from "components/organisms/DoseRuleVersionList";
import DoseRuleNoteList from "components/organisms/DoseRuleNoteList";
import JudgmentBar from "./JudgmentBar";
import GateReasonBox from "./GateReasonBox";
import GateApproveConfirm from "./GateApproveConfirm";
import DoseProductInfoCard from "./DoseProductInfoCard";
import DoseRuleExtractionInfo from "./DoseRuleExtractionInfo";
import { buildReextractConfirm } from "./reextractConfirm";
import { ReextractConfirmBody, buildReextractButtons } from "./ReextractConfirmDialog";
import {
  useDoseRuleDetail,
  useDoseRuleMutations,
  useDoseRuleNotes,
  useDoseRuleVersions,
  useDrugProductLabel,
} from "hooks/doseRules/useDoseRules";
import { AdminDoseRuleDetail, DOSE_RULE_NOTE_MAX_LENGTH } from "services/api/doseRules/types";
import {
  VERSION_NO_MEMO_TEXT,
  countActiveDerived,
  doseRuleStatusLabel,
  formatDateTime,
  formatRuleVersionMeta,
  parseDoseReviewLogNoteRef,
  readGateIssues,
  resolvePrescribeUnit,
} from "utils/doseRule";
import { clearCitationHighlights, highlightTextInContainer } from "utils/domHighlight";
import { sectionDomId } from "common/constants/drugLabelModules";

interface DetailNavState {
  itemCode?: string | null;
  listItems?: Array<{ ruleId: string; itemCode: string | null }>;
  listIndex?: number;
  listSearch?: string;
}

const Root = styled.div`
  height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 0 4px 12px;
`;

const CenterState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

const StyledHandle = styled(PanelResizeHandle)`
  width: 6px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  cursor: col-resize;
  &:hover {
    background-color: ${COLOR_SYSTEM.get("Blue")?.[200]};
  }
`;

const LeftWrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 12px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  overflow: hidden;
`;

const LeftScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

const RightWrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 12px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  overflow: hidden;
`;

const RightScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;

  .dose-rule-citation-highlight {
    background-color: ${COLOR_SYSTEM.get("Yellow")?.[100]};
    box-shadow: 0 0 0 2px ${COLOR_SYSTEM.get("Yellow")?.[200]};
    border-radius: 2px;
  }
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 16px;
  margin-bottom: 12px;
  font-size: 13px;
`;

const MetaItem = styled.div`
  display: flex;
  gap: 6px;
`;

const MetaKey = styled.span`
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  min-width: 64px;
`;

const MetaVal = styled.span`
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  font-weight: 600;
  word-break: break-all;
`;

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  margin: 12px 0 6px;
`;

/**
 * 메모가 없는 현재 버전의 검수 메모 블록 문구(보정 7f). 값이 아니라 **없음의 서술**이라
 * 흐리게 쓴다 — 실제 메모와 같은 무게로 그리면 "메모가 있다"로 오독된다.
 */
const EmptyNote = styled.div`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
`;

const DerivedList = styled.ul`
  margin: 0;
  padding-left: 18px;
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

const DerivedLi = styled.li`
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
`;

/** 전개본 상세의 대표 룰 안내(D-4). */
const DerivedNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin: 4px 0 12px;
  padding: 8px 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Blue")?.[100]};
  background-color: ${COLOR_SYSTEM.get("Blue")?.[10] ?? COLOR_SYSTEM.get("Blue")?.[50]};
  border-radius: 10px;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
`;

/** 형제 spec(AI 다중검증) 자리 — 이번 Phase 밖(D-9 ②). */
const AiVerificationSlot = styled.div``;

const RightNote = styled.div`
  padding: 24px;
  text-align: center;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  font-size: 13px;
`;

/* ── 반려 사유 입력 폼 (ConfirmManager body) ── */

const FormWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 360px;
`;

const NoteTextarea = styled.textarea`
  width: 100%;
  min-height: 96px;
  padding: 10px 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  box-sizing: border-box;
`;

const Counter = styled.div<{ $over?: boolean }>`
  font-size: 12px;
  text-align: right;
  color: ${({ $over }) =>
    $over ? COLOR_SYSTEM.get("Red")?.[600] : COLOR_SYSTEM.get("Gray")?.[400]};
`;

const FormButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

interface RejectNoteFormProps {
  onCancel: () => void;
  onConfirm: (note: string) => void;
}

/** 반려 사유 입력. 사유 필수 + 500자 이내(계획 D11). */
const RejectNoteForm: React.FC<RejectNoteFormProps> = ({ onCancel, onConfirm }) => {
  const [note, setNote] = useState("");
  const trimmed = note.trim();
  const over = note.length > DOSE_RULE_NOTE_MAX_LENGTH;
  const canSubmit = trimmed.length > 0 && !over;

  return (
    <FormWrap>
      <div>반려 사유를 입력해 주세요(필수, {DOSE_RULE_NOTE_MAX_LENGTH}자 이내).</div>
      <NoteTextarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="예: 인용문이 용법용량 절이 아닌 효능효과 절에서 추출됨"
        autoFocus
      />
      <Counter $over={over}>
        {note.length} / {DOSE_RULE_NOTE_MAX_LENGTH}
      </Counter>
      <FormButtons>
        <Button
          buttonType="button"
          type="Text"
          size="Medium"
          variant="Ghost"
          color="Secondary"
          onClick={onCancel}
        >
          취소
        </Button>
        <Button
          buttonType="button"
          type="Text"
          size="Medium"
          variant="Contained"
          color="Negative"
          disabled={!canSubmit}
          disabledColor
          onClick={() => canSubmit && onConfirm(trimmed)}
        >
          반려
        </Button>
      </FormButtons>
    </FormWrap>
  );
};

/** 상세 응답에서 itemCode를 방어적으로 추출(계획 D2). */
const deriveItemCode = (
  stateItemCode: string | null | undefined,
  detail: AdminDoseRuleDetail | undefined
): string | undefined => {
  if (stateItemCode) return stateItemCode;
  const fromTarget = detail?.targetProduct?.itemCode;
  if (typeof fromTarget === "string" && fromTarget) return fromTarget;
  // 대체 경로는 snake_case product_ref.item_code다(현황 §1 — camelCase는 항상 실패했다).
  const fromRef = detail?.productRef?.item_code;
  if (typeof fromRef === "string" && fromRef) return fromRef;
  return undefined;
};

/** 제품명은 `productRef.product_name`이다(dose_products엔 컬럼 없음 — D-2). */
const deriveProductName = (
  detail: AdminDoseRuleDetail | undefined
): string | undefined => {
  const fromRef = detail?.productRef?.product_name;
  if (typeof fromRef === "string" && fromRef) return fromRef;
  return undefined;
};

const DoseRuleDetailPage: React.FC = () => {
  const { ruleId } = useParams<{ ruleId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const navState = (location.state || {}) as DetailNavState;

  const { data: detail, isLoading, isError, refetch } = useDoseRuleDetail(ruleId);
  const { updateStatus, reextract } = useDoseRuleMutations();

  /* 룰 버전 절(Phase 1) — 상세와 **독립된** 쿼리다. 죽어도 본문·판정 바는 살아 있어야 하고,
   * 그래서 아래 `busy` 산식에 이 쿼리들의 상태를 **넣지 않는다**(계획 A-12·§3-2 6항).
   * `notes`는 **모든 룰에서 요청한다**(D-41 쌓인 메모 — 훅 시그니처는 그대로 두고 둘째 인자를 true로).
   * 버전 절에 넘기는 notes 상태는 이력 있는 룰에서만 참으로 거른다(아래 hasVersionHistory 게이트). */
  const versionsQuery = useDoseRuleVersions(ruleId);
  const versions = versionsQuery.data;
  const hasVersionHistory = Array.isArray(versions) && versions.length > 1;
  const notesQuery = useDoseRuleNotes(ruleId, true);
  /* 사유 칸의 `…`는 요청이 떠 있을 때만(보정 7d). notes는 이제 늘 요청하므로 isIdle은 거의 없지만
     판정 기준은 유지한다. 버전 절 노출은 hasVersionHistory로 거른다(버전 1개 룰에 "은퇴 사유를
     불러오지 못했습니다"가 뜨면 거짓말 — D-41). 쌓인 메모 칸은 그 게이트와 무관하게 오류를 보인다. */
  const isNotesLoading = notesQuery.isLoading && !notesQuery.isIdle;

  const itemCode = deriveItemCode(navState.itemCode, detail);
  const productName = deriveProductName(detail);
  const labelQuery = useDrugProductLabel(itemCode);
  const labels = labelQuery.data ?? [];

  const [rightEl, setRightEl] = useState<HTMLElement | null>(null);

  const busy = updateStatus.isLoading || reextract.isLoading;

  // 이전/다음 컨텍스트(계획 D14).
  const listItems = navState.listItems ?? [];
  const listIndex = typeof navState.listIndex === "number" ? navState.listIndex : -1;
  const prevItem = listIndex > 0 ? listItems[listIndex - 1] : null;
  const nextItem =
    listIndex >= 0 && listIndex < listItems.length - 1 ? listItems[listIndex + 1] : null;

  const backToList = () => {
    const search = navState.listSearch ? `?${navState.listSearch}` : "";
    navigate(`/admin/dose-rules${search}`);
  };

  const goToListItem = (
    target: { ruleId: string; itemCode: string | null },
    newIndex: number
  ) => {
    navigate(`/admin/dose-rules/${encodeURIComponent(target.ruleId)}`, {
      state: { ...navState, itemCode: target.itemCode, listIndex: newIndex },
    });
  };

  /** 대표/전개본 상호 링크(D-4) — 목록 컨텍스트 없이 룰 상세로 이동. */
  const goToRule = (id: string) => {
    navigate(`/admin/dose-rules/${encodeURIComponent(id)}`);
  };

  // 라벨 로드 후 용법용량(UD) 절로 스크롤(계획 D4).
  useEffect(() => {
    if (!itemCode || labels.length === 0 || !rightEl) return;
    const raf = requestAnimationFrame(() => {
      try {
        const el = document.getElementById(sectionDomId(itemCode, "UD"));
        el?.scrollIntoView({ block: "start" });
      } catch {
        /* best-effort */
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [itemCode, labels.length, rightEl]);

  // 다른 룰로 전환되면 이전 하이라이트 제거.
  useEffect(() => {
    clearCitationHighlights(rightEl);
  }, [ruleId, rightEl]);

  const handleCitationClick = (text: string) => {
    highlightTextInContainer(rightEl, text);
  };

  /** 승인/반려 성공 후: 다음 룰이 있으면 이동, 없으면 목록 복귀(계획 D15). */
  const afterDecision = () => {
    if (nextItem) goToListItem(nextItem, listIndex + 1);
    else backToList();
  };

  const handleApprove = () => {
    if (!ruleId || busy) return;

    // 차단(gate_blocked) 승인: 검사 이슈를 나열하는 D15 확인 창을 거친다.
    // (옛 기준 차단 룰은 JudgmentBar가 승인 버튼 자체를 숨기므로 여기 닿지 않는다.)
    if (detail?.status === "gate_blocked") {
      const gateIssues = readGateIssues(detail?.provenance);
      const derivedActiveCount = countActiveDerived(detail?.derivedRules);
      ConfirmManager.show({
        title: "차단 룰 승인",
        body: (
          <GateApproveConfirm
            gateIssues={gateIssues}
            derivedActiveCount={derivedActiveCount}
            onCancel={() => ConfirmManager.hide()}
            onConfirm={() => {
              ConfirmManager.hide();
              updateStatus.mutate(
                { ruleId, body: { status: "active" } },
                { onSuccess: afterDecision }
              );
            }}
          />
        ),
      });
      return;
    }

    // 반려(archived) 전개본은 서버가 함께 전이하지 않는다 — 차단 승인 경로와 같은 계산(D-4).
    const cascadeCount = countActiveDerived(detail?.derivedRules);
    ConfirmManager.show({
      title: "룰 승인",
      body:
        cascadeCount > 0
          ? `이 룰을 승인합니다. 전개본 ${cascadeCount}건이 함께 전이될 수 있습니다.`
          : "이 룰을 승인합니다.",
      buttons: [
        <Button
          key="cancel"
          buttonType="button"
          type="Text"
          size="Medium"
          variant="Ghost"
          color="Secondary"
          onClick={() => ConfirmManager.hide()}
        >
          취소
        </Button>,
        <Button
          key="ok"
          buttonType="button"
          type="Text"
          size="Medium"
          variant="Contained"
          color="Primary"
          onClick={() => {
            ConfirmManager.hide();
            updateStatus.mutate(
              { ruleId, body: { status: "active" } },
              { onSuccess: afterDecision }
            );
          }}
        >
          승인
        </Button>,
      ],
    });
  };

  const handleReject = () => {
    if (!ruleId || busy) return;
    ConfirmManager.show({
      title: "룰 반려",
      body: (
        <RejectNoteForm
          onCancel={() => ConfirmManager.hide()}
          onConfirm={(note) => {
            ConfirmManager.hide();
            updateStatus.mutate(
              { ruleId, body: { status: "archived", note } },
              { onSuccess: afterDecision }
            );
          }}
        />
      ),
    });
  };

  const handleReextract = () => {
    if (!ruleId || busy) return;
    // 확인 창 구성(본문·[룰만] 활성·사유)은 순수 함수가 정한다(spec frontend 3, D-14).
    const model = buildReextractConfirm(detail);
    ConfirmManager.show({
      title: "재추출 요청",
      body: <ReextractConfirmBody model={model} />,
      buttons: buildReextractButtons(model, {
        onCancel: () => ConfirmManager.hide(),
        onStep2: () => {
          ConfirmManager.hide();
          reextract.mutate({ ruleId, fromStep: "step2" });
        },
        onStep3: () => {
          ConfirmManager.hide();
          reextract.mutate({ ruleId, fromStep: "step3" });
        },
      }),
    });
  };

  // 이전/다음 키보드 이동(입력 포커스 중엔 무시).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // 확인 창(재추출·승인·반려)이 열려 있으면 화살표 이동을 막는다(D-14) — 안 막으면 아래 룰이
      // 바뀌고 창은 옛 ruleId로 남아 엉뚱한 룰에 재추출·승인이 걸린다.
      if (ConfirmManager.isVisible()) return;
      const tag = (document.activeElement?.tagName || "").toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "ArrowLeft" && prevItem) goToListItem(prevItem, listIndex - 1);
      if (e.key === "ArrowRight" && nextItem) goToListItem(nextItem, listIndex + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevItem, nextItem, listIndex]);

  // 새 기준(criteria_version 있음) 여부 — 옛 기준 차단 룰은 승인 불가(D10·D14).
  const criteriaVersion =
    typeof detail?.provenance?.criteria_version === "string"
      ? (detail.provenance.criteria_version as string)
      : null;
  const isNewCriteria = !!criteriaVersion && criteriaVersion.trim().length > 0;
  const isGateBlocked = detail?.status === "gate_blocked";
  const ruleGateIssues = readGateIssues(detail?.provenance);

  // 전개본 표시·단위 문맥(D-1·D-4).
  const derivedFrom = detail?.derivedFrom ?? null;
  const isDerived = !!derivedFrom;
  const prescribeUnit = resolvePrescribeUnit(detail?.targetProduct, detail?.productRef);
  const perUnit = detail?.targetProduct?.dispense?.per_unit ?? null;

  return (
    <Root>
      <TopBar>
        <Button
          buttonType="button"
          type="Text"
          size="Small"
          variant="Outlined"
          color="Secondary"
          onClick={backToList}
        >
          ← 목록
        </Button>
        <Button
          buttonType="button"
          type="Text"
          size="Small"
          variant="Ghost"
          color="Secondary"
          disabled={!prevItem}
          disabledColor
          onClick={() => prevItem && goToListItem(prevItem, listIndex - 1)}
        >
          이전
        </Button>
        <Button
          buttonType="button"
          type="Text"
          size="Small"
          variant="Ghost"
          color="Secondary"
          disabled={!nextItem}
          disabledColor
          onClick={() => nextItem && goToListItem(nextItem, listIndex + 1)}
        >
          다음
        </Button>
      </TopBar>

      {isLoading ? (
        <CenterState>
          <Spinner />
        </CenterState>
      ) : isError || !detail ? (
        <CenterState>
          <div>
            룰 상세를 불러오지 못했습니다.{" "}
            <Button
              buttonType="button"
              type="Text"
              size="Small"
              variant="Outlined"
              color="Secondary"
              onClick={() => refetch()}
            >
              다시 시도
            </Button>
          </div>
        </CenterState>
      ) : (
        <PanelGroup direction="horizontal" autoSaveId="doseRuleDetail" style={{ flex: 1 }}>
          <Panel defaultSize={46} minSize={28}>
            <LeftWrap>
              <LeftScroll>
                {/* ① 제품 정보 카드(D-2·D-9). */}
                <DoseProductInfoCard
                  targetProduct={detail.targetProduct}
                  productRef={detail.productRef}
                  checks={detail.checks}
                  selectors={detail.selectors}
                  sharedSkus={detail.sharedSkus}
                  sharedSkusTruncated={detail.sharedSkusTruncated}
                  companyName={detail.companyName}
                  baked={detail.baked}
                />

                {/* ② AI 검증 — 형제 spec(2026-09-18 AI 다중검증). 자리만(D-9). */}
                <AiVerificationSlot data-testid="dose-ai-verification-slot" />

                <MetaGrid>
                  <MetaItem>
                    <MetaKey>ruleId</MetaKey>
                    <MetaVal>{detail.ruleId}</MetaVal>
                  </MetaItem>
                  <MetaItem>
                    <MetaKey>상태</MetaKey>
                    <MetaVal>{doseRuleStatusLabel(detail.status)}</MetaVal>
                  </MetaItem>
                  <MetaItem>
                    <MetaKey>class</MetaKey>
                    <MetaVal>{detail.class || "—"}</MetaVal>
                  </MetaItem>
                  <MetaItem>
                    <MetaKey>type</MetaKey>
                    <MetaVal>{detail.type || "—"}</MetaVal>
                  </MetaItem>
                  <MetaItem>
                    <MetaKey>구분</MetaKey>
                    <MetaVal>{detail.isRepresentative ? "대표" : "전개"}</MetaVal>
                  </MetaItem>
                  <MetaItem>
                    <MetaKey>검수자</MetaKey>
                    <MetaVal>{detail.reviewedBy || "—"}</MetaVal>
                  </MetaItem>
                  <MetaItem>
                    <MetaKey>검수일</MetaKey>
                    <MetaVal>{formatDateTime(detail.reviewedAt)}</MetaVal>
                  </MetaItem>
                  {/* 8번째 항목 — 2열 그리드의 마지막 빈 칸을 채운다(계획 L-2). */}
                  <MetaItem>
                    <MetaKey>룰 버전</MetaKey>
                    <MetaVal>
                      {formatRuleVersionMeta(detail.version, detail.versionCount)}
                    </MetaVal>
                  </MetaItem>
                </MetaGrid>

                {/* 룰 버전 절 — 메타 아래·검수 메모 블록 위(계획 L-1). 룰 식별 정보가
                    이력보다 먼저 와야 한다.

                    `key={ruleId}`는 보정 7c다. 이 라우트는 단일 element라 `:ruleId`만 바뀌면
                    컴포넌트가 언마운트되지 않고(설계 결정 10), 그러면 사용자가 접어 둔 절이
                    **다음 룰에서도 접힌 채**로 이어진다 — 오류 문구와 `다시 시도`까지 접힌
                    `<details>` 안에 숨는다. 룰이 바뀌면 절을 새로 태워 기본 상태로 되돌린다. */}
                <DoseRuleVersionList
                  key={ruleId}
                  versions={versions}
                  isLoading={versionsQuery.isLoading}
                  isError={versionsQuery.isError}
                  onRetry={() => versionsQuery.refetch()}
                  notes={notesQuery.data}
                  isNotesLoading={hasVersionHistory && isNotesLoading}
                  isNotesError={hasVersionHistory && notesQuery.isError}
                  onNotesRetry={() => notesQuery.refetch()}
                />

                {/* 검수 메모 블록(보정 7f · 설계 결정 13).
                    **버전이 2개 이상인 룰이면 메모가 없어도 블록을 그린다** — 재추출로 버전이
                    오른 룰의 현재 버전이 바로 "또 지워졌다"가 발화하는 자리이고, 블록이 사라지면
                    "지워졌다"와 "원래 안 썼다"가 구분되지 않는다. 버전 1개 룰(dev 855건)은
                    사라질 이전 메모가 없으므로 기존 조건부 렌더 그대로 둔다.
                    판정은 **상세 응답의 `versionCount`**로 한다 — `reviewNote`와 같은 응답이라
                    버전 목록이 로딩·실패여도 블록이 흔들리지 않는다(4R 재검토).
                    `versionCount`가 없으면(옛 백엔드) 기존 조건부다. */}
                {detail.reviewNote || (detail.versionCount ?? 0) >= 2 ? (
                  <>
                    <SectionTitle>검수 메모</SectionTitle>
                    {detail.reviewNote ? (
                      <div style={{ fontSize: 13 }}>{detail.reviewNote}</div>
                    ) : (
                      <EmptyNote>{VERSION_NO_MEMO_TEXT}</EmptyNote>
                    )}
                  </>
                ) : null}

                {/* 쌓인 메모(D-41) — 버전 수와 무관하게 늘 그린다. 판정 기록발 review 메모를 보인다.
                    versions는 성공일 때만 넘긴다(로딩·실패면 notes.version 폴백 — D-41 순서).
                    noteHref는 본문 접두를 판정 기록 상세 경로로(organism은 라우트를 모른다). */}
                <DoseRuleNoteList
                  notes={notesQuery.data}
                  versions={versionsQuery.isSuccess ? versions : undefined}
                  isLoading={isNotesLoading}
                  isError={notesQuery.isError}
                  onRetry={() => notesQuery.refetch()}
                  noteHref={(note) => {
                    const ref = parseDoseReviewLogNoteRef(note.body);
                    return ref ? `/admin/dose-review-logs/${ref.logId}` : null;
                  }}
                />

                {/* ③ 판정 바(하단 고정)·차단 사유(D-9). */}
                {isGateBlocked ? <GateReasonBox gateIssues={ruleGateIssues} /> : null}

                {/* 전개본이면 대표 룰 안내·링크(D-4). */}
                {isDerived && derivedFrom ? (
                  <DerivedNotice>
                    <span>전개본 — 구간 원본은 대표 룰에 있습니다.</span>
                    <Button
                      buttonType="button"
                      type="Text"
                      size="Small"
                      variant="Outlined"
                      color="Primary"
                      onClick={() => goToRule(derivedFrom)}
                    >
                      대표 룰로 가기
                    </Button>
                  </DerivedNotice>
                ) : null}

                {/* ④ 구간 카드(D-9). */}
                <DoseRuleSegmentPanel
                  content={detail.content ?? {}}
                  rawContent={detail.rawContent}
                  criteriaVersion={criteriaVersion}
                  prescribeUnit={prescribeUnit}
                  perUnit={perUnit}
                  isDerived={isDerived}
                  onCitationClick={handleCitationClick}
                />

                {detail.derivedRules && detail.derivedRules.length > 0 ? (
                  <>
                    <SectionTitle>전개본 {detail.derivedRules.length}건</SectionTitle>
                    <DerivedList>
                      {detail.derivedRules.map((d) => (
                        <DerivedLi key={d.ruleId}>
                          <Button
                            buttonType="button"
                            type="Text"
                            size="XSmall"
                            variant="Ghost"
                            color="Primary"
                            onClick={() => goToRule(d.ruleId)}
                          >
                            {d.standardCode || d.ruleId}
                          </Button>
                          <span>
                            {doseRuleStatusLabel(d.status)}
                            {d.ratio != null ? ` · ratio ${d.ratio}` : ""}
                          </span>
                        </DerivedLi>
                      ))}
                    </DerivedList>
                  </>
                ) : null}

                {/* ⑤ 추출 정보(D-3·D-9). */}
                <DoseRuleExtractionInfo
                  provenance={detail.provenance}
                  isDerived={isDerived}
                  ruleId={detail.ruleId}
                  derivedFrom={derivedFrom}
                />
              </LeftScroll>

              <JudgmentBar
                status={detail.status}
                isNewCriteria={isNewCriteria}
                busy={busy}
                onApprove={handleApprove}
                onReject={handleReject}
                onReextract={handleReextract}
              />
            </LeftWrap>
          </Panel>

          <StyledHandle />

          <Panel defaultSize={54} minSize={28}>
            <RightWrap>
              <ContentSearchBar targetElement={rightEl} placeholder="허가사항 원문에서 검색…" />
              <RightScroll ref={setRightEl}>
                {labelQuery.isLoading ? (
                  <RightNote>
                    <Spinner />
                  </RightNote>
                ) : !itemCode ? (
                  <RightNote>품목코드가 없어 허가사항 원문을 불러올 수 없습니다.</RightNote>
                ) : labelQuery.isError ? (
                  <RightNote>허가사항 원문을 불러오지 못했습니다(왼쪽 판정은 계속 가능).</RightNote>
                ) : labels.length === 0 ? (
                  <RightNote>이 품목의 허가사항 원문이 없습니다.</RightNote>
                ) : (
                  <DrugLabelInfo
                    labelInfo={labels}
                    itemCode={itemCode}
                    productName={productName}
                  />
                )}
              </RightScroll>
            </RightWrap>
          </Panel>
        </PanelGroup>
      )}
    </Root>
  );
};

export default DoseRuleDetailPage;
